import {
  fetchIssues,
  fetchIssue,
  getIssueProblemLogs,
  postIssue,
  updateIssue,
  fetchRelatedIssues,
  fetchAttachedIssue,
  deleteIssue,
  linkProblemLogToIssue,
  fetchProblemLogsForIssues,
  fetchNewIssueIdForProblemLog,
  unlinkFaultFromIssue,
  fetchPotentialCleanPoints,
  fetchIssueCategories,
} from '../api/issues'
import { useQuery, useQueryClient, useMutation } from 'react-query'
import { useUserInfo, useUserPlant } from './User'
import { FAULT_REPORT_QUERY_KEY } from './Faults'

const ISSUES_QUERY_KEY = 'issues'

// Issue create/update/delete/(un)link mutations all change data embedded in
// the /tracer/faults response (fault.attachedIssue) as well as the issues
// list itself -- both need invalidating or callers see stale data until a
// full page reload resets the react-query cache.
const invalidateIssuesAndFaults = (queryClient) => {
  queryClient.invalidateQueries([ISSUES_QUERY_KEY])
  queryClient.invalidateQueries([FAULT_REPORT_QUERY_KEY])
}

// Controls whether the Incident Management issue edit/create forms show the
// original 4 tabs (Problem Description, Possible Root Causes, Direct Root
// Causes, Actions) or the trimmed-down 4M/Actions tabs from Figma. Driven by
// the deploy-time ISSUE_CREATION_INTERFACE env var, surfaced via
// userInfo.features.issue_creation_interface (see src/core/issues.py).
export const useIsCompressedIssueCreationInterface = () => {
  const { userInfo } = useUserInfo()
  return userInfo?.features?.issue_creation_interface === 'compressed'
}

export const useGetIssues = ({ make_model_id = null, enabled = true } = {}) => {
  const [userPlant] = useUserPlant()
  const {
    status,
    data: issues,
    error,
    isLoading,
  } = useQuery(
    [ISSUES_QUERY_KEY, userPlant, make_model_id],
    async () => {
      const result = await fetchIssues(userPlant, make_model_id)

      return result
    },
    {
      refetchOnWindowFocus: false,
      enabled,
    },
  )
  return { status, issues, error, isLoading }
}

export const useGetIssue = (issueId) => {
  const {
    status,
    data: issues,
    error,
    isLoading,
  } = useQuery(
    [ISSUES_QUERY_KEY, issueId],
    async () => {
      const result = await fetchIssue(issueId)

      return result
    },
    {
      refetchOnWindowFocus: false,
    },
  )
  return { status, issues, error, isLoading }
}

export const useGetIssueProblemLogs = (issueId) => {
  const {
    status,
    data: problemLogs,
    error,
    isLoading,
  } = useQuery(
    [ISSUES_QUERY_KEY, 'problem-logs', issueId],
    async () => {
      const result = await getIssueProblemLogs(issueId)

      return result
    },
    {
      refetchOnWindowFocus: false,
    },
  )
  return { status, problemLogs, error, isLoading }
}

export const usePostIssue = () => {
  const queryClient = useQueryClient()

  return useMutation(({ issue }) => postIssue(issue), {
    onSuccess: () => invalidateIssuesAndFaults(queryClient),
  })
}

export const useUpdateIssue = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ issue_id, issue }) => {
      return updateIssue(issue_id, issue)
    },
    {
      onSuccess: () => invalidateIssuesAndFaults(queryClient),
    },
  )
}

export const useFetchRelatedIssues = () => {
  return useMutation(
    (
      problem_log_id,
      fault_short_code,
      component_id,
      device_name,
      harness_id,
      userPlant,
    ) =>
      fetchRelatedIssues(
        problem_log_id,
        fault_short_code,
        component_id,
        device_name,
        harness_id,
        userPlant,
      ),
  )
}

export const useFetchAttachedIssue = () => {
  return useMutation((problem_log_id) => fetchAttachedIssue(problem_log_id))
}

export const useDeleteIssue = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ issue_id }) => {
      return deleteIssue(issue_id)
    },
    {
      onSuccess: () => invalidateIssuesAndFaults(queryClient),
    },
  )
}

export const useLinkProblemLogToIssue = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ issue_id, pl_id }) => linkProblemLogToIssue(issue_id, pl_id),
    {
      onSuccess: () => invalidateIssuesAndFaults(queryClient),
    },
  )
}

export const useFetchProblemLogsForIssues = () => {
  return useMutation(({ issue_ids }) => {
    return fetchProblemLogsForIssues(issue_ids)
  })
}

export const useFetchNewIssueIdForProblemLog = () => {
  return useMutation(({ problem_log_id, issue_category_id }) =>
    fetchNewIssueIdForProblemLog(problem_log_id, issue_category_id),
  )
}

export const useUnlinkFaultFromIssue = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ issue_id, fault_id }) => unlinkFaultFromIssue({ issue_id, fault_id }),
    {
      onSuccess: () => invalidateIssuesAndFaults(queryClient),
    },
  )
}

export const useFetchPotentialCleanPoints = () => {
  return useMutation(({ issue_id }) => fetchPotentialCleanPoints({ issue_id }))
}

export const useFetchIssueCategories = ({ make_model_id }) => {
  const {
    status,
    data: categories,
    error,
    isLoading,
  } = useQuery(
    [ISSUES_QUERY_KEY, 'categories', make_model_id],
    async () => {
      const result = await fetchIssueCategories({ make_model_id })
      return result
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!make_model_id,
    },
  )
  return { status, categories, error, isLoading }
}
